import { useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { toast } from "sonner"
import { roomApi, roomSchemas } from "@/api/client/room"
import { getErrorMessage } from "@/api/instance"
import { PropertyHeader } from "@/components/properties/PropertyHeader"
import { RoomForm, type RoomFormValues } from "@/components/properties/RoomForm"

export function RoomAddPage() {
  const navigate = useNavigate()
  const { propertyId: propertyIdParam } = useParams()
  const propertyId = propertyIdParam ? Number(propertyIdParam) : undefined
  const [isSubmitting, setIsSubmitting] = useState(false)

  const roomCreateMutation = roomApi.useRoomCreate({
    mutation: {
      onSuccess: ({ id }) => {
        toast.success("部屋を登録しました。")
        if (propertyId !== undefined) {
          navigate(`/properties/${propertyId}/rooms/${id}`)
        } else {
          navigate("/properties", { state: { shouldRefetch: true } })
        }
      },
      onError: (error) => {
        toast.error("部屋の登録に失敗しました。", { description: getErrorMessage(error) })
      },
    },
  })

  const handleSubmit = async (values: RoomFormValues) => {
    if (propertyId === undefined) {
      toast.error("物件情報が見つかりません。")
      return
    }

    setIsSubmitting(true)
    try {
      const normalizedFloor = Number(values.floor) || 0
      const normalizedSize = values.size ?? 0
      const data = roomSchemas.roomCreateBody.parse({
        propertyId,
        name: values.name,
        layout: values.layout ?? "",
        size: Number.isFinite(normalizedSize) ? normalizedSize : 0,
        floor: Number.isFinite(normalizedFloor) ? normalizedFloor : 0,
      })

      await roomCreateMutation.mutateAsync({ data })
    } catch (error) {
      console.error("Error submitting room:", error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    if (propertyId !== undefined) {
      navigate(`/properties/${propertyId}`, { state: { shouldRefetch: true } })
    } else {
      navigate("/properties")
    }
  }

  if (!propertyId) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <div className="text-slate-600">物件IDが不正です。</div>
      </div>
    )
  }

  return (
    <div className="space-y-5">
      <PropertyHeader propertyId={propertyId} />

      <RoomForm
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isSubmitting={isSubmitting || roomCreateMutation.isPending}
      />
    </div>
  )
}
